import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  ListGroup,
  Pagination,
  Container,
  ProgressBar,
} from 'react-bootstrap';

import {
  useGetOpeningStoreStocksQuery,
  useUpdateOpeningStoreStockMutation,
} from '../../slices/openingStoreStocksApiSlice';

import { useGetProductsQuery } from '../../slices/productsApiSlice';

import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const OpeningStoreStockSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementName, setReplacementName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [previewResults, setPreviewResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const [isReplacing, setIsReplacing] = useState(false);
  const [progress, setProgress] = useState(0);

  const [diffPreview, setDiffPreview] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [focusedBox, setFocusedBox] = useState('search');

  const { data: openingStoreStocksData, isLoading, error } = useGetOpeningStoreStocksQuery();

  const {
    data: productsData = { products: [], pages: 1 },
    isLoading: loadingProducts,
    error: errorProducts,
  } = useGetProductsQuery({ pageNumber });

  const [updateOpeningStoreStock] = useUpdateOpeningStoreStockMutation();

  // ------------------------------------------------------------
  // PRODUCT SELECTION
  // ------------------------------------------------------------
  const handleProductSelect = (product) => {
    if (focusedBox === 'search') {
      setSearchTerm(product.name);
    } else {
      setReplacementName(product.name);
      setSelectedProduct(product);
    }

    setPreviewResults([]);
    setSelectedItems({});
    setSelectAll(false);
  };

  // ------------------------------------------------------------
  // SEARCH MATCHES
  // ------------------------------------------------------------
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = openingStoreStocksData?.openingStoreStocks?.flatMap((openingStoreStock) =>
      openingStoreStock.openingStoreStockItems
        .filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((item) => ({
          openingStoreStockId: openingStoreStock._id,
          itemId: item.product.toString(), // unique key
          originalName: item.name,
          qty: item.qty,
          price: item.price,
          date: openingStoreStock.date,
        }))
    );

    setPreviewResults(matches || []);
    setSelectedItems({});
    setSelectAll(false);
  };

  // ------------------------------------------------------------
  // SELECT ALL / UNSELECT ALL
  // ------------------------------------------------------------
  const toggleSelectAll = () => {
    const newState = !selectAll;
    setSelectAll(newState);

    if (newState) {
      const all = {};
      previewResults.forEach((item) => {
        all[item.itemId] = true;
      });
      setSelectedItems(all);
    } else {
      setSelectedItems({});
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      const updated = { ...prev };
      if (updated[itemId]) delete updated[itemId];
      else updated[itemId] = true;
      return updated;
    });
  };

  // ------------------------------------------------------------
  // DIFF PREVIEW
  // ------------------------------------------------------------
  const buildDiffPreview = (replacementProduct) => {
    const diffs = previewResults
      .filter((item) => selectedItems[item.itemId])
      .map((item) => ({
        originalName: item.originalName,
        originalPrice: item.price,
        newName: replacementProduct.name,
        newPrice: replacementProduct.price,
      }));

    setDiffPreview(diffs);
  };

  // ------------------------------------------------------------
  // APPLY REPLACEMENT (MODE 5)
  // ------------------------------------------------------------
  const handleReplace = async () => {
    if (!replacementName.trim()) return toast.error('Replacement name required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const replacementProduct =
      productsData.products.find(
        (p) => p.name.toLowerCase() === replacementName.toLowerCase()
      ) ||
      productsData.products.find((p) =>
        p.name.toLowerCase().includes(replacementName.toLowerCase())
      );

    if (!replacementProduct) {
      return toast.error(`Product "${replacementName}" not found`);
    }

    const selectedIds = Object.keys(selectedItems);
    if (selectedIds.length === 0) {
      return toast.info('No items selected for replacement');
    }

    buildDiffPreview(replacementProduct);
    setIsReplacing(true);

    try {
      const groupedByOpeningStoreStock = {};

      previewResults.forEach((item) => {
        if (selectedItems[item.itemId]) {
          if (!groupedByOpeningStoreStock[item.openingStoreStockId])
            groupedByOpeningStoreStock[item.openingStoreStockId] = [];

          groupedByOpeningStoreStock[item.openingStoreStockId].push(item.itemId);
        }
      });

      const totalOpeningStoreStocksToUpdate = Object.keys(groupedByOpeningStoreStock).length;
      let processed = 0;

      for (const openingStoreStock of openingStoreStocksData.openingStoreStocks) {
        const selectedProductIds = groupedByOpeningStoreStock[openingStoreStock._id];
        if (!selectedProductIds) continue;

        const updatedItems = openingStoreStock.openingStoreStockItems.map((item) => {
          const id = item.product.toString();
          const isSelected = selectedProductIds.includes(id);

          if (!isSelected) return item;

          // MODE 5 — FULL PRODUCT REPLACEMENT
          return {
            product: replacementProduct._id,
            name: replacementProduct.name,
            price: replacementProduct.price,
            images: Array.isArray(replacementProduct.images)
              ? replacementProduct.images
              : [],
            qty: item.qty,
            itemCost: item.itemCost, // cost stays same
          };
        });

        await updateOpeningStoreStock({
          openingStoreStockId: openingStoreStock._id,
          openingStoreStockItems: updatedItems,
          supplierDetails: openingStoreStock.supplierDetails,
          note: openingStoreStock.note,
          status: openingStoreStock.status,
          date: openingStoreStock.date,
          expiryDate: openingStoreStock.expiryDate,
        }).unwrap();

        processed += 1;
        setProgress(Math.round((processed / totalOpeningStoreStocksToUpdate) * 100));
      }

      toast.success(`Replaced selected items with "${replacementProduct.name}"`);

      setPreviewResults([]);
      setSelectedItems({});
      setSelectAll(false);
      setDiffPreview([]);
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
      setProgress(0);
    }
  };

  // ------------------------------------------------------------
  // FILTER PRODUCTS
  // ------------------------------------------------------------
  const activeFilter =
    focusedBox === 'search' ? searchTerm : replacementName;

  const filteredProducts = productsData.products.filter((p) =>
    p.name.toLowerCase().includes(activeFilter.toLowerCase())
  );

  // ------------------------------------------------------------
  // RETURN BLOCK (UI)
  // ------------------------------------------------------------
  return (
    <Container fluid>
      <Row>
        <Col md={3}>
          <h5 className="mb-3">Product List</h5>
          {loadingProducts ? (
            <Loader />
          ) : errorProducts ? (
            <Message variant="danger">
              {errorProducts?.data?.message || 'Failed to load products'}
            </Message>
          ) : (
            <>
              <ListGroup>
                {filteredProducts.map((product) => (
                  <ListGroup.Item
                    key={product._id}
                    action
                    onClick={() => handleProductSelect(product)}
                  >
                    {product.name} — D{product.price?.toFixed(2)}
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <Pagination className="mt-3">
                {[...Array(productsData.pages).keys()].map((x) => (
                  <Pagination.Item
                    key={x + 1}
                    active={x + 1 === pageNumber}
                    onClick={() => setPageNumber(x + 1)}
                  >
                    {x + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </>
          )}
        </Col>

        <Col md={9}>
          <Row className="mb-3">
            <Col><h1>OpeningStoreStocks Search & Replace</h1></Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Search for product name in openingStoreStocks"
                value={searchTerm}
                onFocus={() => setFocusedBox('search')}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Replacement product name"
                value={replacementName}
                onFocus={() => setFocusedBox('replace')}
                onChange={(e) => {
                  setReplacementName(e.target.value);
                  setSelectedProduct(null);
                }}
              />
            </Col>

            <Col md={4}>
              <Button
                variant="primary"
                onClick={handleSearch}
                className="me-2"
                disabled={!searchTerm.trim() || isReplacing}
              >
                Preview Matches
              </Button>
              <Button
                variant="success"
                onClick={handleReplace}
                disabled={!replacementName.trim() || isReplacing}
              >
                Replace Selected
              </Button>
            </Col>
          </Row>

          {selectedProduct && (
            <Alert variant="info">
              Selected replacement: <strong>{selectedProduct.name}</strong> — D
              {selectedProduct.price.toFixed(2)}
            </Alert>
          )}

          {diffPreview.length > 0 && (
            <Alert variant="secondary">
              <strong>Preview changes:</strong>
              <ul className="mb-0">
                {diffPreview.map((d, idx) => (
                  <li key={idx}>
                    {d.originalName} (D{d.originalPrice.toFixed(2)}) →{' '}
                    {d.newName} (D{d.newPrice.toFixed(2)})
                  </li>
                ))}
              </ul>
            </Alert>
          )}

          {isReplacing && (
            <ProgressBar
              now={progress}
              label={`${progress}%`}
              className="mb-3"
            />
          )}

          {isLoading ? (
            <Loader />
          ) : error ? (
            <Message variant="danger">
              {error?.data?.message || 'Error loading openingStoreStocks'}
            </Message>
          ) : previewResults.length === 0 ? (
            <Alert variant="warning">No matching items found.</Alert>
          ) : (
            <>
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={selectAll}
                onChange={toggleSelectAll}
                className="mb-2"
              />

              <Table striped bordered hover responsive className="table-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>DATE</th>
                    <th>ORIGINAL NAME</th>
                    <th>QTY</th>
                    <th>PRICE</th>
                  </tr>
                </thead>
                <tbody>
                  {previewResults.map((item) => (
                    <tr key={item.itemId}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          checked={!!selectedItems[item.itemId]}
                          onChange={() => toggleSelectItem(item.itemId)}
                        />
                      </td>
                      <td>{new Date(item.date).toLocaleDateString()}</td>
                      <td>{item.originalName}</td>
                      <td>{item.qty}</td>
                      <td>D{item.price.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OpeningStoreStockSearchReplaceScreen;
